import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import { getAccountActivities } from '../api';
import type { ActivityDetail } from '../types';

interface ActivityTabProps {
  accountId: string;
}

const PAGE_SIZE = 20;

const ACTIVITY_ICONS: Record<string, string> = {
  contract_created: '📄',
  contract_updated: '✏️',
  contract_reassigned: '🔀',
  contract_renewed: '🔄',
  contract_expired: '⏰',
  invoice_created: '🧾',
  invoice_updated: '✏️',
  invoice_reassigned: '🔀',
  invoice_paid: '💵',
  quote_created: '📋',
  quote_updated: '✏️',
  quote_reassigned: '🔀',
  invoice_overdue: '⚠️',
  site_created: '🏢',
  site_updated: '✏️',
  ticket_created: '🎫',
  ticket_resolved: '✅',
  sensor_added: '📡',
  sensor_reassigned: '🔀',
  sensor_removed: '❌',
  subscription_sensor_added: '🔗',
  subscription_sensor_removed: '✂️',
  subscription_sensors_bulk_added: '⛓️',
  user_added: '👤',
  note_added: '📝',
  callback_requested: '📞',
  callback_completed: '✅',
  callback_cancelled: '❌',
  installation_code_generated: '🔑',
  installation_code_revoked: '🚫',
  installation_code_sent: '📨',
  installation_access_granted: '🔓',
  installation_access_denied: '🔒',
  installation_checklist_updated: '📋',
  installation_photo_uploaded: '📷',
  installation_signature_captured: '✍️',
  installation_terms_acknowledged: '📜',
  installation_preflight_completed: '✈️',
  installation_tools_confirmed: '🔧',
  installation_materials_confirmed: '📦',
  installation_job_submitted: '🏁',
  installation_job_deleted: '🗑️',
  installation_dispatched_to_field_nation: '🚀',
  installation_sensor_verification_submitted: '🔬',
  crm_call: '📞',
  crm_meeting: '🤝',
  crm_email: '📧',
  crm_discovery_session: '🔍',
  crm_proposal_presentation: '📊',
  crm_note: '📝',
  account_archived: '🗃️',
  account_unarchived: '📂',
  default: '•',
};

export default function ActivityTab({ accountId }: ActivityTabProps) {
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-activities', accountId, page, typeFilter],
    queryFn: () =>
      getAccountActivities(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        type: typeFilter === 'all' ? undefined : typeFilter,
      }),
  });

  if (isLoading) {
    return <ActivityTabSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading activity</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const activities = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              {total} activit{total !== 1 ? 'ies' : 'y'} found
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="contract_created">Contracts Created</SelectItem>
                <SelectItem value="contract_updated">Contracts Updated</SelectItem>
                <SelectItem value="quote_created">Quotes Created</SelectItem>
                <SelectItem value="quote_updated">Quotes Updated</SelectItem>
                <SelectItem value="invoice_created">Invoices Created</SelectItem>
                <SelectItem value="invoice_updated">Invoices Updated</SelectItem>
                <SelectItem value="reassigned">Reassignments</SelectItem>
                <SelectItem value="ticket_created">Tickets</SelectItem>
                <SelectItem value="site_created">Sites</SelectItem>
                <SelectItem value="note_added">Notes</SelectItem>
                <SelectItem value="callback">Callbacks</SelectItem>
                <SelectItem value="installation">Installations</SelectItem>
                <SelectItem value="crm">CRM Activities</SelectItem>
                <SelectItem value="account_archived">Account Archived</SelectItem>
                <SelectItem value="account_unarchived">Account Unarchived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No activity found
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {activities.map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityItem({ activity }: { activity: ActivityDetail }) {
  const icon = ACTIVITY_ICONS[activity.activityType] || ACTIVITY_ICONS.default;
  const date = new Date(activity.createdAt);
  const isToday = new Date().toDateString() === date.toDateString();

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="text-lg w-6 text-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{activity.description}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">
            {isToday
              ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : formatDate(activity.createdAt)}
          </span>
          {activity.relatedEntityType && (
            <Badge variant="outline" className="text-xs">
              {activity.relatedEntityType}
            </Badge>
          )}
          {activity.performedBy && (
            <span className="text-xs text-muted-foreground">
              by {activity.performedBy}
            </span>
          )}
        </div>
      </div>
      {activity.relatedEntityId && activity.relatedEntityType && (
        activity.relatedEntityType === 'installation_job' ? (
          <Link
            to={`/operations/installations/${activity.relatedEntityId}`}
            className="text-atek-blue hover:underline text-xs"
          >
            View
          </Link>
        ) : (
          <a
            href={getEntityRoute(activity.relatedEntityType, activity.relatedEntityId)}
            target="_self"
            className="text-atek-blue hover:underline text-xs"
          >
            View
          </a>
        )
      )}
    </div>
  );
}

function getEntityRoute(entityType: string, entityId: string): string {
  if (entityType === 'installation_job') {
    return `/operations/installations/${entityId}`;
  }
  const routeBuilders: Record<string, ((id: string) => string) | undefined> = {
    contract: legacyRoutes.contract,
    invoice: legacyRoutes.invoice,
    quote: legacyRoutes.quote,
    site: legacyRoutes.site,
    ticket: legacyRoutes.ticket,
    sensor: legacyRoutes.sensor,
    user: legacyRoutes.user,
  };
  const builder = routeBuilders[entityType];
  return builder ? builder(entityId) : '#';
}

function ActivityTabSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-24 mt-1" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-6 w-6 rounded" />
              <div className="flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/4 mt-2" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
